import { motion } from "framer-motion";
import { Truck, ExternalLink, MapPin } from "lucide-react";
import { inr } from "@/lib/api";

const STEPS = ["confirmed", "packed", "shipped", "out_for_delivery", "delivered"];
const STEP_LABELS = { confirmed: "Confirmed", packed: "Packed", shipped: "Shipped", out_for_delivery: "Out for Delivery", delivered: "Delivered" };

export const PAYMENT_TYPE = { card: "Prepaid · Card", wallet: "Prepaid · Wallet", cod: "Cash on Delivery" };

export const StatusTimeline = ({ status }) => {
  const current = STEPS.indexOf(status);
  if (["cancelled", "pending", "returned", "awaiting_confirmation"].includes(status)) {
    const labels = { cancelled: "Cancelled", pending: "Payment pending", returned: "Returned", awaiting_confirmation: "Awaiting your confirmation" };
    const colors = { cancelled: "text-red-500", pending: "text-amber-500", returned: "text-red-500", awaiting_confirmation: "text-amber-500" };
    return <span className={`text-[10px] uppercase tracking-[0.25em] ${colors[status]}`} data-testid="order-timeline">{labels[status]}</span>;
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="order-timeline">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-1.5">
          <span className={`text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 ${i <= current ? "bg-gold text-white" : "bg-zinc-100 text-zinc-400"}`}>
            {STEP_LABELS[s]}
          </span>
          {i < STEPS.length - 1 && <span className={`w-3 h-px ${i < current ? "bg-gold" : "bg-zinc-200"}`} />}
        </div>
      ))}
    </div>
  );
};

export const OrderCard = ({ o }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-zinc-200 p-6 md:p-8" data-testid={`order-card-${o.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div>
        <p className="font-mono text-xs text-zinc-500">#{o.id.slice(0, 8).toUpperCase()}</p>
        <p className="text-xs text-zinc-400 mt-1">{new Date(o.created_at).toLocaleString("en-IN")}</p>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 border ${o.payment_method === "cod" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
          data-testid={`order-pay-type-${o.id}`}
        >
          {PAYMENT_TYPE[o.payment_method] || "Prepaid"}
        </span>
        <p className="font-serif text-2xl text-gold">{inr(o.total)}</p>
      </div>
    </div>
    <StatusTimeline status={o.status} />
    {o.status === "awaiting_confirmation" && o.payment_method === "cod" && o.confirm_token && (
      <div className="mt-5 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 px-5 py-4" data-testid={`order-confirm-${o.id}`}>
        <p className="text-xs text-amber-700 flex-1 min-w-[200px]">Please confirm this COD order so we can pack it.</p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/cod-confirm?token=${o.confirm_token}`}
          data-testid={`order-confirm-btn-${o.id}`}
          className="bg-gold text-white text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 hover:bg-champagne transition-colors duration-300"
        >
          Confirm Order
        </a>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/cod-cancel?token=${o.confirm_token}`}
          data-testid={`order-cancel-btn-${o.id}`}
          className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-500 transition-colors duration-300"
        >
          Cancel
        </a>
      </div>
    )}
    <div className="mt-6 space-y-2">
      {o.items.map((i) => (
        <div key={i.product_id} className="flex justify-between text-sm">
          <span className="text-zinc-600">{i.name}{i.variant ? ` — ${i.variant}` : ""} × {i.quantity}</span>
          <span className="text-zinc-400">{inr(i.price * i.quantity)}</span>
        </div>
      ))}
    </div>
    {o.shipping_address?.line1 && (
      <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500" data-testid={`order-address-${o.id}`}>
        <MapPin size={13} className="text-gold mt-0.5 shrink-0" />
        <span>
          {o.shipping_address.name}, {o.shipping_address.line1}
          {o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}, {o.shipping_address.city}, {o.shipping_address.state} — {o.shipping_address.pincode}
        </span>
      </div>
    )}
    {o.tracking_id && (
      <div className="mt-6 flex flex-wrap items-center gap-4 bg-pink-50 border border-pink-100 px-5 py-4" data-testid={`order-tracking-${o.id}`}>
        <Truck size={16} className="text-gold" />
        <span className="text-sm text-zinc-700">{o.courier_partner || "Courier"} — <span className="font-medium">{o.tracking_id}</span></span>
        {o.tracking_url && (
          <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gold hover:text-champagne transition-colors duration-300">
            Track <ExternalLink size={12} />
          </a>
        )}
      </div>
    )}
  </motion.div>
);
